import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Plus, Pencil, Trash2, RefreshCw, Cpu, X, Zap,
  ShieldCheck, BarChart3, Route, Code2, Clock,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';
import { HeisenbergPanel } from '../../components/admin/HeisenbergPanel';

const ALGO_ICONS = {
  motor_heisenberg: Route,
  triple_check_cuantico: ShieldCheck,
  filtro_pareto: BarChart3,
};

const ALGO_COLORS = {
  motor_heisenberg: { bg: 'from-blue-600/20 to-cyan-600/20', border: 'border-cyan-700/50', accent: 'text-cyan-400', dot: 'bg-cyan-500' },
  triple_check_cuantico: { bg: 'from-orange-600/20 to-amber-600/20', border: 'border-orange-700/50', accent: 'text-orange-400', dot: 'bg-orange-500' },
  filtro_pareto: { bg: 'from-purple-600/20 to-fuchsia-600/20', border: 'border-purple-700/50', accent: 'text-purple-400', dot: 'bg-purple-500' },
};

const DEFAULT_COLOR = { bg: 'from-slate-600/20 to-gray-600/20', border: 'border-gray-600/50', accent: 'text-emerald-400', dot: 'bg-emerald-500' };

const AdminAlgorithms = () => {
  const [algorithms, setAlgorithms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [form, setForm] = useState({ name: '', version: '1.0', description: '', logic_description: '', active: true });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getAlgorithms();
      setAlgorithms(data);
    } catch {
      toast({ title: 'Error cargando algoritmos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: '', version: '1.0', description: '', logic_description: '', active: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Error', description: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    try {
      if (editing) {
        await adminAPI.updateAlgorithm(editing, form);
        toast({ title: 'Algoritmo actualizado' });
      } else {
        await adminAPI.createAlgorithm(form);
        toast({ title: 'Algoritmo creado' });
      }
      resetForm();
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este algoritmo?')) return;
    try {
      await adminAPI.deleteAlgorithm(id);
      toast({ title: 'Eliminado' });
      load();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleEdit = (a) => {
    setForm({
      name: a.name || '',
      version: a.version || '1.0',
      description: a.description || '',
      logic_description: a.logic_description || '',
      active: a.active,
    });
    setEditing(a.id);
    setShowForm(true);
  };

  const handleToggle = async (a) => {
    const id = a.id;
    setToggling(id);
    try {
      await adminAPI.updateAlgorithm(id, { is_active: !a.active });
      setAlgorithms(prev => prev.map(al => al.id === id ? { ...al, active: !al.active } : al));
      toast({ title: !a.active ? 'Algoritmo activado' : 'Algoritmo desactivado' });
    } catch {
      toast({ title: 'Error al cambiar estado', variant: 'destructive' });
    } finally {
      setToggling(null);
    }
  };

  const activeCount = algorithms.filter(a => a.active).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl border border-cyan-700/30">
              <Cpu className="w-7 h-7 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="admin-algorithms-title">Algoritmos</h1>
              <p className="text-gray-400 text-sm mt-0.5">
                {algorithms.length} registrados · {activeCount} activos
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={load} className="text-gray-400 hover:text-white" data-testid="refresh-algorithms">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-cyan-600 hover:bg-cyan-700" data-testid="new-algorithm-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Algoritmo
            </Button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <Card className="bg-gray-800/80 border-gray-700 p-6 backdrop-blur-sm" data-testid="algorithm-form">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Code2 className="w-5 h-5 text-cyan-400" />
                {editing ? 'Editar Algoritmo' : 'Nuevo Algoritmo'}
              </h3>
              <Button variant="ghost" size="sm" onClick={resetForm} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 text-sm">Nombre *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ej: Motor Heisenberg" className="bg-gray-900 border-gray-600 text-white mt-1" data-testid="algo-name" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Version</Label>
                <Input value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))}
                  placeholder="1.0" className="bg-gray-900 border-gray-600 text-white mt-1" data-testid="algo-version" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-300 text-sm">Descripcion</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Que hace este algoritmo..." className="bg-gray-900 border-gray-600 text-white mt-1 min-h-[60px]" data-testid="algo-desc" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-300 text-sm">Formula / Logica</Label>
                <Textarea value={form.logic_description} onChange={e => setForm(p => ({ ...p, logic_description: e.target.value }))}
                  placeholder="distance = geocode(A,B); if distance <= 30km ..."
                  className="bg-gray-900 border-gray-600 text-white font-mono text-sm mt-1 min-h-[60px]" data-testid="algo-logic" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700" data-testid="algo-save">
                {editing ? 'Actualizar' : 'Crear Algoritmo'}
              </Button>
              <Button variant="outline" onClick={resetForm} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                Cancelar
              </Button>
            </div>
          </Card>
        )}

        {/* Algorithm Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        ) : algorithms.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700 p-12 text-center">
            <Cpu className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Sin algoritmos</h2>
            <p className="text-gray-400">Crea tu primer algoritmo para comenzar</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="algorithms-grid">
            {algorithms.map((algo) => {
              const colors = ALGO_COLORS[algo.id] || DEFAULT_COLOR;
              const IconComp = ALGO_ICONS[algo.id] || Zap;
              const isToggling = toggling === algo.id;

              return (
                <Card
                  key={algo.id}
                  className={`bg-gradient-to-br ${colors.bg} ${colors.border} border overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-black/20`}
                  data-testid={`algorithm-card-${algo.id}`}
                >
                  <div className="p-5">
                    {/* Top row: icon + name + toggle */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gray-900/60 border border-gray-700/50`}>
                          <IconComp className={`w-6 h-6 ${colors.accent}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold text-lg leading-tight" data-testid={`algo-name-${algo.id}`}>
                            {algo.name}
                          </h3>
                          <span className="text-gray-500 text-xs font-mono">v{algo.version || '1.0'}</span>
                        </div>
                      </div>

                      {/* Toggle Switch */}
                      <div className="flex items-center gap-2" data-testid={`algo-toggle-${algo.id}`}>
                        <span className={`text-xs font-semibold ${algo.active ? 'text-green-400' : 'text-gray-500'}`}>
                          {algo.active ? 'ON' : 'OFF'}
                        </span>
                        <Switch
                          checked={algo.active}
                          onCheckedChange={() => handleToggle(algo)}
                          disabled={isToggling}
                          className={`${algo.active ? 'bg-green-500' : 'bg-gray-600'}`}
                          data-testid={`algo-switch-${algo.id}`}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    {algo.description && (
                      <p className="text-gray-300 text-sm leading-relaxed mb-3" data-testid={`algo-description-${algo.id}`}>
                        {algo.description}
                      </p>
                    )}

                    {/* Logic formula */}
                    {algo.logic_description && (
                      <div className="bg-gray-900/50 rounded-lg px-3 py-2 mb-4 border border-gray-700/40">
                        <p className="text-gray-400 text-xs font-mono leading-relaxed truncate" title={algo.logic_description}>
                          <Code2 className="w-3 h-3 inline mr-1.5 text-gray-500" />
                          {algo.logic_description}
                        </p>
                      </div>
                    )}

                    {/* Footer: status indicator + actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-700/30">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${algo.active ? 'bg-green-400 animate-pulse' : 'bg-gray-600'}`} />
                        <span className={`text-xs font-medium ${algo.active ? 'text-green-400' : 'text-gray-500'}`}>
                          {algo.active ? 'Ejecutandose' : 'Detenido'}
                        </span>
                        {algo.created_at && (
                          <span className="text-gray-600 text-xs flex items-center gap-1 ml-2">
                            <Clock className="w-3 h-3" />
                            {new Date(algo.created_at).toLocaleDateString('es-MX')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(algo)}
                          className="text-gray-400 hover:text-white h-8 w-8 p-0" data-testid={`edit-algo-${algo.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(algo.id)}
                          className="text-gray-400 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-algo-${algo.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
        {algorithms.find(a => a.id === 'motor_heisenberg')?.params && (
          <HeisenbergPanel algo={algorithms.find(a => a.id === 'motor_heisenberg')} onSaved={load} />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAlgorithms;
